import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { FaChartLine, FaShieldAlt, FaServer } from "react-icons/fa";
import { motion } from "framer-motion";

export const ClientsProposition = (props) => {
  const { t, i18n } = useTranslation();
  const [visibleSections, setVisibleSections] = useState([]);
  const sectionRefs = useRef([]);
  const [maxHeight, setMaxHeight] = useState(0);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 0);

  // Helper function to get the appropriate icon for each section
  const getIconForSection = (index) => {
    switch(index) {
      case 0: return <FaChartLine />;      // C-Level
      case 1: return <FaServer />;         // IT (moved to second position)
      case 2: return <FaShieldAlt />;      // InfoSec Team (moved to third position)
      default: return <FaChartLine />;
    }
  };

  // Helper function to get blur effect colors for each section
  const getBlurColors = (index) => {
    switch(index) {
      case 0: return [
        'rgba(60, 188, 161, 0.4)', 
        'rgba(217, 31, 70, 0.25)'
      ];
      case 1: return [  // IT colors (was case 2)
        'rgba(235, 94, 46, 0.35)', 
        'rgba(217, 31, 70, 0.3)'
      ];
      case 2: return [  // InfoSec colors (was case 1)
        'rgba(207, 41, 159, 0.35)', 
        'rgba(60, 188, 161, 0.3)'
      ];
      default: return [
        'rgba(60, 188, 161, 0.4)', 
        'rgba(217, 31, 70, 0.25)'
      ];
    }
  };

  // Handle window resize and track window width
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    // Add resize event listener
    window.addEventListener('resize', handleResize);
    
    // Initial execution to set the width
    handleResize();
    
    // Cleanup event listener
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Reset visible sections when window width changes (add this new useEffect)
  useEffect(() => {
    // Reset visible sections when layout changes
    setVisibleSections([]);
  }, [windowWidth]);

  // Calculate max height of description text for desktop card layout
  useEffect(() => {
    const calculateMaxHeight = () => {
      if (windowWidth > 992) {
        const textContainers = document.querySelectorAll('.proposition-card .text-container');
        if (textContainers.length > 0) {
          let maxHeight = 0;
          textContainers.forEach(container => {
            const height = container.clientHeight;
            if (height > maxHeight) {
              maxHeight = height;
            }
          });
          setMaxHeight(maxHeight);
        }
      } else {
        setMaxHeight(0);
      }
    };

    calculateMaxHeight();
    window.addEventListener('resize', calculateMaxHeight);
    
    return () => {
      window.removeEventListener('resize', calculateMaxHeight);
    };
  }, [visibleSections, windowWidth]);

  // Setup intersection observer to detect when sections come into view
  useEffect(() => {
    // Add CSS for the animation effects that aren't in the external CSS
    const style = document.createElement('style');
    style.innerHTML = `
      .proposition-container {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        gap: 100px;
        margin: 0 auto;
        max-width: 1200px;
        padding: 20px 0;
      }
      .proposition-section {
        opacity: 0;
        transform: translateY(30px);
        transition: opacity 1s ease, transform 1s ease;
        display: flex;
        align-items: center;
      }
      .proposition-section.visible {
        opacity: 1;
        transform: translateY(0);
      }
      .blur-icon-container {
        flex: 0 0 33.333%;
        position: relative;
        height: 180px;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .blur-effect {
        position: absolute;
        border-radius: 50%;
        filter: blur(30px);
        opacity: 0;
        transition: opacity 1.5s ease;
      }
      .blur-effect.visible {
        opacity: 1;
      }
      .blur-effect-1 {
        width: 100px;
        height: 100px;
        top: 30px;
        left: 50%;
        transform: translateX(-50%);
        z-index: 1;
      }
      .blur-effect-2 {
        width: 80px;
        height: 80px;
        bottom: 20px;
        left: 45%;
        z-index: 1;
      }
      .section-icon {
        position: relative;
        z-index: 2;
        font-size: 4.5rem;
        color: white;
        filter: drop-shadow(0 0 15px rgba(255, 255, 255, 0.4));
        opacity: 0;
        transform: scale(0.8);
        transition: opacity 1s ease 0.5s, transform 1s ease 0.5s;
      }
      .section-icon.visible {
        opacity: 1;
        transform: scale(1);
      }
      .text-container {
        flex: 0 0 33.333%;
        padding: 0 20px;
      }
      .empty-space {
        flex: 0 0 33.333%;
      }
      
      /* Desktop layout - 3 column cards */
      @media (min-width: 993px) {
        .proposition-container {
          flex-direction: row;
          flex-wrap: nowrap;
          gap: 20px;
        }
        .proposition-card {
          flex: 1;
          display: flex;
          flex-direction: column;
          background: rgba(30, 30, 30, 0.5);
          border-radius: 10px;
          overflow: hidden;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
          height: 100%;
        }
        .proposition-card:hover {
          transform: translateY(-10px);
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
        }
        .proposition-card .blur-icon-container {
          flex: 0 0 auto;
          width: 100%;
          margin-bottom: 20px;
        }
        .proposition-card .text-container {
          flex: 1;
          width: 100%;
          padding: 0 20px 20px;
          display: flex;
          flex-direction: column;
        }
        .proposition-card .proposition-description {
          text-align: left;
        }
      }
      
      /* Tablet layout */
      @media (max-width: 992px) {
        .blur-icon-container {
          flex: 0 0 33.333%;
        }
        .text-container {
          flex: 0 0 66.667%;
        }
        .empty-space {
          display: none;
        }
        .proposition-section {
          margin-bottom: 60px;
        }
      }
      
      /* Mobile layout */
      @media (max-width: 768px) {
        .proposition-section {
          width: 100%;
          flex-direction: column;
        }
        .blur-icon-container {
          flex: 0 0 auto;
          width: 100%;
          margin-bottom: 20px;
        }
        .text-container {
          flex: 0 0 auto;
          width: 100%;
          text-align: center;
        }
        .proposition-container {
          gap: 60px;
        }
        .section-icon {
          font-size: 3.5rem;
        }
        .blur-effect-1 {
          width: 100px;
          height: 100px;
        }
        .blur-effect-2 {
          width: 80px;
          height: 80px;
        }
        #clientsproposition h3:after {
          left: 50%;
          transform: translateX(-50%);
        }
      }
      
      .proposition-description {
        hyphens: auto;
        word-break: normal;
        overflow-wrap: break-word;
        -webkit-hyphens: auto;
        -ms-hyphens: auto;
      }
      
      /* Set language-specific hyphenation rules */
      :lang(ru) .proposition-description {
        hyphens: auto;
        word-break: normal;
      }
      
      :lang(en) .proposition-description {
        hyphens: auto;
        word-break: normal;
      }
    `;
    document.head.appendChild(style);
    
    // Setup intersection observer
    const callback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const sectionIndex = parseInt(entry.target.dataset.index);
          setVisibleSections(prev => 
            prev.includes(sectionIndex) ? prev : [...prev, sectionIndex]
          );
        }
      });
    };

    const observer = new IntersectionObserver(callback, {
      root: null,
      rootMargin: '0px',
      threshold: 0.2,
    });

    sectionRefs.current.forEach(section => {
      if (section) observer.observe(section);
    });

    return () => {
      document.head.removeChild(style);
      sectionRefs.current.forEach(section => {
        if (section) observer.unobserve(section);
      });
    };
  }, [sectionRefs, windowWidth]); // Added windowWidth dependency here

  // Get sections and reorder them to move IT to second position and InfoSec to third
  const originalSections = t("ClientsProposition.sections", { returnObjects: true }) || [];
  
  // Create a new array with reordered sections
  const clientsPropositionDetails = [...originalSections];
  
  // Only reorder if we have at least 3 sections
  if (clientsPropositionDetails.length >= 3) {
    // Store the IT section (index 2 in original order)
    const itSection = clientsPropositionDetails[2];
    // Store the InfoSec section (index 1 in original order)
    const infoSecSection = clientsPropositionDetails[1];
    // Swap positions
    clientsPropositionDetails[1] = itSection;
    clientsPropositionDetails[2] = infoSecSection;
  }
  
  return (
    <div id="clientsproposition">
      <div className="container">
        <div className="section-title text-center">
          <h2>{t("ClientsProposition.Title")}</h2>
          <h3>{t("ClientsProposition.Title2")}</h3>
          <p>{t("ClientsProposition.Subtitle")}</p>       
        </div>
        <div className="proposition-container">
          {clientsPropositionDetails.map((section, index) => {
            const blurColors = getBlurColors(index);
            
            // Desktop view (cards)
            if (windowWidth > 992) {
              return (
                <motion.div 
                  key={`section-${index}`} 
                  ref={el => sectionRefs.current[index] = el}
                  data-index={index}
                  className={`proposition-card ${visibleSections.includes(index) ? 'visible' : ''}`}
                  whileHover={{ y: -10 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <div className="blur-icon-container">
                    <div 
                      className={`blur-effect blur-effect-1 ${visibleSections.includes(index) ? 'visible' : ''}`} 
                      style={{backgroundColor: blurColors[0]}}
                    ></div>
                    <div 
                      className={`blur-effect blur-effect-2 ${visibleSections.includes(index) ? 'visible' : ''}`} 
                      style={{backgroundColor: blurColors[1]}}
                    ></div>
                    <div className={`section-icon ${visibleSections.includes(index) ? 'visible' : ''}`}>
                      {getIconForSection(index)}
                    </div>
                  </div>
                  <div 
                    className="text-container"
                    style={{ minHeight: maxHeight > 0 ? `${maxHeight}px` : 'auto' }}
                  >
                    <h3 className="proposition-subtitle" lang={i18n.language}>{section.name}</h3>
                    <p className="proposition-description" lang={i18n.language}>{section.description}</p>
                  </div>
                </motion.div>
              );
            }
            
            // Tablet/Mobile view (original layout)
            return (
              <div 
                key={`section-${index}`} 
                ref={el => sectionRefs.current[index] = el}
                data-index={index}
                className={`proposition-section ${visibleSections.includes(index) ? 'visible' : ''}`}
              >
                <div className="blur-icon-container">
                  <div 
                    className={`blur-effect blur-effect-1 ${visibleSections.includes(index) ? 'visible' : ''}`} 
                    style={{backgroundColor: blurColors[0]}}
                  ></div>
                  <div 
                    className={`blur-effect blur-effect-2 ${visibleSections.includes(index) ? 'visible' : ''}`} 
                    style={{backgroundColor: blurColors[1]}}
                  ></div>
                  <div className={`section-icon ${visibleSections.includes(index) ? 'visible' : ''}`}>
                    {getIconForSection(index)}
                  </div>
                </div>
                <div className="text-container">
                  <h3 className="proposition-subtitle" lang={i18n.language}>{section.name}</h3>
                  <p className="proposition-description" lang={i18n.language}>{section.description}</p>
                </div>
                <div className="empty-space"></div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};